import type {
  ExposureLineItemsListLineItemWithInvoiceInfo,
  ExposureLineItemsListPayableType,
  UpdateExposureLineItemsRequestCoverageDecisionOption,
} from '~/server_shared/openapi/data-contracts';

export interface Invoice {
  id: number;
  claim_id: number;
  document_id: number;
  invoice_number: string;
  invoice_date: string | null;
  vendor: string | null;
  vendor_contact_id: number | null;
  invoice_currency: string | null;
  submitted_amount: number;
  covered_amount: number;
  not_covered_amount: number;
  excluded_amount: number;
  extra: Record<string, unknown>;
  line_items: LineItem[];
  created_at: string;
  updated_at: string;
}

export interface LineItem {
  id: number;
  description: string;
  amount: number;
  payable_type?: ExposureLineItemsListPayableType | null;
  coverage_id: number | null;
  coverage_key: string | null;
  coverage_decision: UpdateExposureLineItemsRequestCoverageDecisionOption;
  covered_amount: number;
  exposure_id?: number | null;
  explanation?: string | null;
  amount_currency?: string | null;
  item_date?: string | null;
  invoice_date?: string | null;
  // Currency conversion fields
  conversion_date?: string | null;
  conversion_rate?: number | null;
  base_amount?: number | null;
  is_locked?: boolean;
  payment_request_id?: number | null;
  payment_financial_status?: string | null;
  third_party_amount?: number | null;
  entry_type?: string;
}

export type LineItemWithInvoiceInfo = ExposureLineItemsListLineItemWithInvoiceInfo;

export interface SummaryItem {
  key: string;
  label: string;
  value: number;
  display: boolean;
}

/**
 * Parameters for the settlement formula, enabling frontend recalculation on edit.
 *
 * The backend owns which rate/deductible/limit apply (business logic).
 * The frontend uses these to recompute summary instantly when line items change.
 * Only sum(covered_amount) changes during editing; all other params are exposure-level.
 *
 * NOTE: In the future this client-side recalculation may be replaced by
 * backend-generated CEL (Common Expression Language) expressions that the
 * frontend evaluates directly, removing the need for a hardcoded formula.
 */
export interface SettlementParams {
  coinsurance_insurer_percentage: number;
  deductible: number;
  has_co_insurance: boolean;
  has_deductible: boolean;
  // When true, expenses are summed into the subtotal alongside indemnity (master /
  // legacy Fast Cover semantics). When false, expenses are tracked separately and
  // don't get co-insurance / deductible. Lets the FE branch its live-recalc to
  // match what the BE will compute on save.
  subtotal_includes_expenses: boolean;
}

export interface ExposureLineItemsWithSummary {
  line_items: LineItemWithInvoiceInfo[];
  summary: SummaryItem[];
  settlement_params: SettlementParams;
}

export interface CoverageBreakdown {
  coverage_id: number;
  coverage_key: string;
  submitted_amount: number;
  covered_amount: number;
  not_covered_amount: number;
  co_insurance: number;
  eligible_for_reimbursement: number;
}

export interface InvoiceCoveragesBreakdown {
  invoice_id: number;
  coverages: CoverageBreakdown[];
}

export type CoverageDecisionType = 'covered' | 'excluded' | 'not_covered';

export const COVERAGE_DECISIONS: { value: CoverageDecisionType; description: string }[] = [
  {
    value: 'covered',
    description: 'Covered',
  },
  {
    value: 'not_covered',
    description: 'Not Covered',
  },
  {
    value: 'excluded',
    description: 'Excluded',
  },
];

/**
 * InvoiceRecommendation - AI-generated invoice analysis data.
 * Sourced from CliveAction task_data for invoice_analysis_recommendation actions.
 * Used by GenericInvoiceAnalysisContainer when processing AI recommendations.
 */
export interface InvoiceRecommendation {
  cliveActionId: number;
  invoice_date: string | null;
  invoice_number: string | null;
  vendor: string | null;
  document_claim_internal_id?: number;
  line_items: InvoiceRecommendationLineItem[];
  coverages: InvoiceRecommendationCoverage[];
}

export interface InvoiceRecommendationLineItem {
  id: string;
  description: string;
  amount: number;
  coverage_key: string | null;
  coverage_decision: UpdateExposureLineItemsRequestCoverageDecisionOption | null;
  covered_amount: number | null;
  explanation: string | null;
  item_date: string | null;
  exposure_id?: number | null;
}

export interface InvoiceRecommendationCoverage {
  coverage_key: string;
  coverage_display_name: string;
}
